import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Play, Pause, SkipForward, SkipBack, ListMusic, Lock } from "lucide-react";
import { api, mediaUrl, TYPE_META } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { CustomPage } from "@/pages/Misc";

function usePlayer(queue, pauseSeconds) {
  const audio = useRef(null);
  const timer = useRef(null);
  const [idx, setIdx] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const current = queue[idx];
  const src = current ? mediaUrl(current.audio_url) : "";
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!audio.current || !src || audio.current.src === src) return;
    audio.current.src = src;
    audio.current.play().catch(() => setPlaying(false));
  }, [src]);
  const toggle = () => {
    if (idx === -1) {
      audio.current.src = mediaUrl(queue[0].audio_url);
      audio.current.play().catch(() => setPlaying(false));
      setIdx(0);
    } else if (playing) audio.current.pause();
    else audio.current.play();
  };
  const go = (n) => {
    clearTimeout(timer.current);
    if (n >= 0 && n < queue.length) setIdx(n);
  };
  const ended = () => {
    if (idx < queue.length - 1) timer.current = setTimeout(() => setIdx((i) => i + 1), (pauseSeconds ?? 4) * 1000);
    else setIdx(-1);
  };
  const el = <audio ref={audio} onEnded={ended} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} className="hidden" />;
  return { idx, playing, current, toggle, go, el };
}

const Ctrl = ({ onClick, disabled, label, testid, children }) => (
  <button onClick={onClick} disabled={disabled} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 transition-opacity disabled:opacity-30" aria-label={label} data-testid={testid}>{children}</button>
);

function Player({ p, queue, cfg }) {
  const { idx, playing, current, toggle, go, el } = usePlayer(queue, cfg.pause_seconds);
  return (
    <>
      <div className="mt-10 rounded-[28px] bg-white/10 p-6" data-testid="playlist-player">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] opacity-70">{current ? `Now playing · ${idx + 1} of ${queue.length}` : `${queue.length} affirmations ready`}</p>
        <p className="mt-2 font-heading text-2xl" data-testid="playlist-now-playing">{current ? current.title : "Press play and breathe"}</p>
        <div className="mt-6 flex items-center gap-3">
          <Ctrl onClick={() => go(idx - 1)} disabled={idx <= 0} label="Previous" testid="playlist-prev"><SkipBack size={18} /></Ctrl>
          <button onClick={toggle} disabled={!queue.length} className="flex h-16 flex-1 items-center justify-center gap-2 rounded-full bg-bt-bg text-lg font-semibold text-bt-primary transition-transform active:scale-[0.98] disabled:opacity-50" data-testid="playlist-play-button">
            {playing ? <><Pause size={20} /> Pause</> : <><Play size={20} /> {idx === -1 ? "Play session" : "Resume"}</>}
          </button>
          <Ctrl onClick={() => go(idx + 1)} disabled={idx === -1 || idx >= queue.length - 1} label="Next" testid="playlist-next"><SkipForward size={18} /></Ctrl>
        </div>
        {el}
      </div>
      <ol className="mt-8 space-y-2" data-testid="playlist-track-list">
        {p.tracks.map((t, i) => (
          <li key={t.slug}>
            <button onClick={() => t.audio_url && go(queue.indexOf(t))} className={`flex w-full items-center gap-4 rounded-2xl bg-white/10 p-4 text-left transition-colors hover:bg-white/15 ${current?.slug === t.slug ? "ring-2 ring-[color:var(--bt-background)]" : ""}`} data-testid={`playlist-track-${t.slug}`}>
              <span className="w-6 font-heading text-lg opacity-70">{i + 1}</span>
              <span className="flex-1"><span className="block font-medium">{t.title}</span><span className="text-xs opacity-70">{TYPE_META[t.type]}{t.duration ? ` · ${t.duration}` : ""}</span></span>
            </button>
          </li>
        ))}
      </ol>
    </>
  );
}

function LockedPreview({ p, cfg }) {
  return (
    <div className="mt-10" data-testid="playlist-locked">
      <div className="rounded-[28px] bg-white/10 p-6">
        <Lock className="mb-3" size={22} />
        <p className="text-lg">{cfg.locked_message}</p>
        <Link to="/membership" className="mt-5 inline-flex min-h-[52px] items-center rounded-full bg-bt-bg px-7 font-semibold text-bt-primary" data-testid="playlist-unlock">Unlock with membership</Link>
      </div>
      <ol className="mt-8 space-y-2 opacity-60">
        {p.tracks.map((t, i) => (
          <li key={t.slug} className="flex items-center gap-4 rounded-2xl bg-white/5 p-4"><span className="w-6 font-heading text-lg">{i + 1}</span><span className="flex-1 font-medium">{t.title}</span><Lock size={14} /></li>
        ))}
      </ol>
    </div>
  );
}

export default function Playlist() {
  const { slug } = useParams();
  const { settings } = useSite();
  const { user } = useAuth();
  const cfg = settings.playlists || {};
  const [p, setP] = useState(undefined);
  useEffect(() => {
    api.get(`/playlists/${slug}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [slug, user?.is_member, user?.role]);
  if (p === null) return <CustomPage notFound />;
  if (!p) return <div className="min-h-[60vh]" />;
  const queue = p.tracks.filter((t) => t.audio_url);
  return (
    <div className="bg-bt-primary text-bt-bg" data-testid="playlist-page">
      <div className="container-bt py-14 sm:py-20">
        <div className="mx-auto max-w-2xl">
          <p className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] opacity-75"><ListMusic size={16} /> {cfg.title}</p>
          <h1 className="text-4xl sm:text-5xl" style={{ color: "var(--bt-background)" }} data-testid="playlist-title">{p.title}</h1>
          <p className="mt-4 text-lg opacity-85">{p.description || cfg.intro}</p>
          {p.locked ? <LockedPreview p={p} cfg={cfg} /> : <Player p={p} queue={queue} cfg={cfg} />}
        </div>
      </div>
    </div>
  );
}
